import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Tag, Percent, Gift, Heart } from 'lucide-react';
import { Badge } from '../ui/badge';

const offers = [
  {
    id: 'honeymoon',
    title: 'Honeymoon Escape',
    description: 'Celebrate love with champagne, spa treatments, and candlelit dinner',
    discount: '20% OFF',
    validTill: 'Valid till March 2026',
    icon: Heart,
    image: 'https://images.pexels.com/photos/33914530/pexels-photo-33914530.jpeg',
    isNew: true,
  },
  {
    id: 'corporate',
    title: 'Corporate Retreat',
    description: 'Team building packages with conference facilities and group activities',
    discount: '15% OFF',
    validTill: 'For groups of 10+',
    icon: Tag,
    image: 'https://images.unsplash.com/photo-1745549670488-6852ef218009',
    isNew: false,
  },
  {
    id: 'weekend',
    title: 'Weekend Getaway',
    description: 'Two nights stay with breakfast, dinner, and guided nature walks',
    discount: 'Save ₹5,000',
    validTill: 'Fri-Sun stays only',
    icon: Gift,
    image: 'https://images.pexels.com/photos/29347337/pexels-photo-29347337.jpeg',
    isNew: true,
  },
];

export function OffersPreview() {
  return (
    <section className="section-padding bg-muted/30" data-testid="offers-preview-section">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="overline text-emerald-700 dark:text-emerald-400 mb-3">Special Packages</p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
            Exclusive <span className="text-emerald-700 dark:text-emerald-400">Offers</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Discover our handpicked packages designed to create unforgettable moments.
          </p>
        </motion.div>

        {/* Offers Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {offers.map((offer, index) => (
            <motion.div
              key={offer.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group bg-card rounded-sm overflow-hidden border border-border hover:border-emerald-700/50 transition-all hover:shadow-xl"
              data-testid={`offer-card-${offer.id}`}
            >
              {/* Image */}
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={offer.image}
                  alt={offer.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-emerald-950/30" />
                
                {/* Badges */}
                <div className="absolute top-4 left-4 flex gap-2">
                  {offer.isNew && (
                    <Badge className="bg-gold text-emerald-950 hover:bg-gold">New</Badge>
                  )}
                  <Badge variant="secondary" className="bg-emerald-900 text-white">
                    <Percent className="w-3 h-3 mr-1" />
                    {offer.discount}
                  </Badge>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <offer.icon className="w-5 h-5 text-gold" />
                  <h3 className="font-serif text-xl text-foreground">{offer.title}</h3>
                </div>
                <p className="text-muted-foreground text-sm mb-4">{offer.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{offer.validTill}</span>
                  <Link 
                    to={`/offers#${offer.id}`}
                    className="text-emerald-700 dark:text-emerald-400 text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all"
                  >
                    View Details <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* View All */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center mt-12"
        >
          <Link to="/offers" className="btn-gold inline-flex items-center gap-2" data-testid="view-all-offers-btn">
            View All Offers
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
